import React, { useState, useEffect } from 'react';
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import { useNavigate } from 'react-router-dom';
import { FiCopy, FiCheckCircle, FiArrowLeft, FiX, FiArrowRight } from 'react-icons/fi';

const ShieldIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill={`${color}20`} />
    </svg>
);
const PhoneIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <path d="M9 6h1m4 0h1m-5 4h1m4 0h1m-5 4h1m4 0h1" strokeWidth="1.5" />
        <circle cx="12" cy="18" r="1" />
    </svg>
);
const BankIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M3 10h18M5 10v11M19 10v11M12 10v11" />
        <path d="M3 10l9-7 9 7" />
    </svg>
);
const SupportIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
);

const PaymentModal = ({ isOpen, onClose, colors, initialAmount = 0, orderId, manifestId, onNotify, refreshData }) => {
    const { userId, socket, user } = useAuth();
    const navigate = useNavigate();
    
    // UI & Data States
    const [screen, setScreen] = useState('selection'); 
    const [localCurrency, setLocalCurrency] = useState('INR');
    const [paymentMethod, setPaymentMethod] = useState(''); 
    const [userInput, setUserInput] = useState(initialAmount);
    const [txid, setTxid] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [financeData, setFinanceData] = useState([]);
    const [copyFeedback, setCopyFeedback] = useState(false);

    // Extract tenant identifier
    const tenantId = localStorage.getItem("adminId") || (user && user.adminId) || "global";

    const rates = { "INR": 1.0, "USD": 83.15, "RUB": 0.90, "PHP": 1.48, "EUR": 90.10, "GBP": 105.20 };
    const MIN_AMOUNT_INR = 1000;

    // Dynamic Theme Mapping
    const theme = {
        bg: colors?.bg || '#0f1113',
        text: colors?.text || '#ffffff',
        subText: colors?.muted || '#94a3b8',
        card: colors?.card || '#1e2227',
        border: colors?.border || '#334155',
        brand: colors?.brand || '#10b981',
        accent: '#3b82f6'
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // FIX CULPRIT: Changed endpoint from /api/finance/admin to user endpoint /api/finance/settings
                const res = await api.get("/api/finance/settings", {
                    headers: tenantId !== "global" ? { "x-tenant-id": tenantId } : {}
                });
                const data = Array.isArray(res.data) ? res.data : [];
                setFinanceData(data);
                
                // Flexible property matching for crypto
                const protocols = data.filter(i => 
                    (i.type === 'crypto' && (i.crypto?.blockchain || i.crypto?.address)) ||
                    i.blockchain || i.network
                );

                if (protocols.length > 0 && !paymentMethod) {
                    const defaultChain = protocols[0].crypto?.blockchain || protocols[0].blockchain || protocols[0].network;
                    setPaymentMethod(defaultChain || '');
                }
            } catch (err) {
                console.error("Data fetch error:", err);
            }
        };
        if (isOpen) fetchSettings();
    }, [isOpen, tenantId]);

    useEffect(() => { 
        if(isOpen) {
            setUserInput(initialAmount);
            setScreen('selection');
            setTxid('');
        }
    }, [isOpen, initialAmount]);

    if (!isOpen) return null;

    const currentRate = rates[localCurrency] || 1.0;
    const numericInput = parseFloat(userInput) || 0;
    const inrValue = numericInput * currentRate;
    const isBelowMinimum = inrValue < MIN_AMOUNT_INR;
    const assetValue = inrValue / 91;

    // Handle nested or flat data schema matching
    const selectedRecord = financeData.find(i => {
        const chain = i.crypto?.blockchain || i.blockchain || i.network;
        return chain === paymentMethod;
    });

    const activeAddress = selectedRecord?.crypto?.address || selectedRecord?.address || "Network Address Pending";

    const handleCopy = (text) => {
        if (!text || text.includes("Pending")) return;
        navigator.clipboard.writeText(text);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
    };

    const handleSupportRedirect = () => {
        const rawId = manifestId || orderId;
        let cleanId = "PENDING";
        if (rawId && rawId !== "PENDING") {
            cleanId = String(rawId).slice(-8).toUpperCase();
        }
        onClose();
        navigate('/profile', { 
            state: { 
                openChat: true,
                initialMessage: `Clearance for Manifest Ref. (MFST // ${cleanId})`
            } 
        });
    };

    const handleVerifyClick = async () => {
        if (!txid || txid.trim().length < 5) {
            return onNotify("Please enter a valid Reference ID", "error");
        }
        if (!userId) {
            return onNotify("Session expired. Please re-login", "error");
        }
        
        const finalAmount = Number(inrValue.toFixed(2));
        setIsSubmitting(true);
        try {
            const payload = {
                userId: userId,
                amount: finalAmount,
                txid: txid.trim(), 
                method: paymentMethod || "DIRECT_ENTRY",
                currency: "INR",
                orderId: orderId || null,
                tenantId: tenantId !== "global" ? tenantId : undefined
            };

            const response = await api.post("/api/transactions/deposit", payload, {
                headers: tenantId !== "global" ? { "x-tenant-id": tenantId } : {}
            });
            if (response.data.success || response.status === 201) {
                if (socket) {
                    socket.emit("new_payment_submitted", { userId, amount: finalAmount, txid: txid.trim(), tenantId });
                }
                onNotify("Entry Submitted for Audit", "success");
                setScreen('verifying');
                if (refreshData) refreshData(); 
            }
        } catch (err) {
            onNotify(err.response?.data?.message || "Submission Failed", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        if (screen === 'selection') onClose();
        else setScreen('selection');
    };

    const cryptoItems = financeData.filter(i => i.type === 'crypto' || i.crypto || i.blockchain);

    return (
        <div style={styles.overlay} data-tenant-scope={tenantId}>
            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes modalPop { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                @keyframes toastIn { from { opacity: 0; transform: translate(-50%, 20px); } to { opacity: 1; transform: translate(-50%, 0); } }
                .custom-scroll::-webkit-scrollbar { width: 4px; }
                .custom-scroll::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 10px; }
                .method-card:hover { transform: translateY(-3px); background: ${theme.card} !important; border-color: ${theme.brand} !important; box-shadow: 0 10px 20px rgba(0,0,0,0.2); }
                .method-card:active { transform: scale(0.98); }
            `}</style>

            <div style={{...styles.modalCard, backgroundColor: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, animation: 'modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'}}>
                
                {copyFeedback && (
                    <div style={{...styles.toast, backgroundColor: theme.brand, animation: 'toastIn 0.3s ease-out'}}>
                        <FiCheckCircle size={14} /> COPIED TO CLIPBOARD
                    </div>
                )}

                <div style={{...styles.headerAction, background: theme.card, borderBottom: `1px solid ${theme.border}`}}>
                    <button onClick={handleBack} style={{...styles.iconBtn, color: theme.text}}>
                        <FiArrowLeft /> BACK
                    </button>
                    <span style={{...styles.headerTitle, color: theme.text}}>REPLENISH ACCOUNT</span>
                    <button onClick={onClose} style={{...styles.iconBtn, color: theme.text}}>
                        <FiX size={18} />
                    </button>
                </div>

                <div className="custom-scroll" style={{...styles.body, maxHeight: '80vh', overflowY: 'auto'}}>
                    
                    {screen === 'selection' && (
                        <div style={styles.selectionBody}>
                            <h3 style={{...styles.selectionTitle, color: theme.text}}>Select Channel</h3>
                            
                            <button className="method-card" style={{...styles.methodBtn, backgroundColor: 'rgba(255,255,255,0.02)', borderColor: theme.border, color: theme.text}} onClick={() => setScreen('crypto')}>
                                <div style={{...styles.iconCircle, background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)'}}>
                                    <ShieldIcon color="#3b82f6" />
                                </div>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <div style={{fontWeight: '800'}}>Secure Blockchain (Rapid)</div>
                                    <div style={{fontSize: '11px', opacity: 0.6}}>USDT • BTC • ETH</div>
                                </div>
                                <FiArrowRight opacity={0.5} />
                            </button>

                            <button className="method-card" style={{...styles.methodBtn, backgroundColor: 'rgba(255,255,255,0.02)', borderColor: theme.border, color: theme.text}} onClick={() => setScreen('apps')}>
                                <div style={{...styles.iconCircle, background: 'rgba(167, 139, 250, 0.1)', border: '1px solid rgba(167, 139, 250, 0.2)'}}>
                                    <PhoneIcon color="#a78bfa" />
                                </div>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <div style={{fontWeight: '800'}}>Local Apps</div>
                                    <div style={{fontSize: '11px', opacity: 0.6}}>UPI • GPay • PhonePe</div>
                                </div>
                                <FiArrowRight opacity={0.5} />
                            </button>

                            <button className="method-card" style={{...styles.methodBtn, backgroundColor: 'rgba(255,255,255,0.02)', borderColor: theme.border, color: theme.text}} onClick={() => setScreen('bank')}>
                                <div style={{...styles.iconCircle, background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)'}}>
                                    <BankIcon color="#f59e0b" />
                                </div>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <div style={{fontWeight: '800'}}>Bank Transfer</div>
                                    <div style={{fontSize: '11px', opacity: 0.6}}>IMPS • NEFT • RTGS</div>
                                </div>
                                <FiArrowRight opacity={0.5} />
                            </button>

                            <button className="method-card" style={{...styles.methodBtn, backgroundColor: 'rgba(255,255,255,0.02)', borderColor: theme.border, color: theme.text}} onClick={handleSupportRedirect}>
                                <div style={{...styles.iconCircle, background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)'}}>
                                    <SupportIcon color="#ffffff" />
                                </div>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <div style={{fontWeight: '800'}}>Live Support</div>
                                    <div style={{fontSize: '11px', opacity: 0.6}}>Manual Verification</div>
                                </div>
                                <FiArrowRight opacity={0.5} />
                            </button>
                        </div>
                    )}

                    {screen === 'crypto' && (
                        <>
                            <div style={styles.row}>
                                <select style={{...styles.input, backgroundColor: theme.card, color: theme.text, borderColor: theme.border}} value={localCurrency} onChange={(e) => setLocalCurrency(e.target.value)}>
                                    {Object.keys(rates).map(c => <option key={c} value={c} style={{background: theme.card}}>{c}</option>)}
                                </select>
                                <select style={{...styles.input, backgroundColor: theme.card, color: theme.text, borderColor: theme.border}} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                    {cryptoItems.length === 0 ? (
                                        <option disabled>No Protocols</option>
                                    ) : (
                                        <>
                                            <option value="" disabled>Select Protocol</option>
                                            {cryptoItems.map((item, idx) => {
                                                const chainName = item.crypto?.blockchain || item.blockchain || item.network || `Protocol ${idx+1}`;
                                                return (
                                                    <option key={item._id || idx} value={chainName} style={{background: theme.card}}>
                                                        {chainName}
                                                    </option>
                                                );
                                            })}
                                        </>
                                    )}
                                </select>
                            </div>

                            <input type="number" style={{...styles.inputFull, backgroundColor: theme.card, color: theme.text, borderColor: isBelowMinimum ? '#ef4444' : theme.border}} value={userInput} onChange={(e) => setUserInput(e.target.value)} placeholder="0.00"/>
                            
                            <div style={{...styles.conversionBox, backgroundColor: theme.card, borderColor: theme.border}}>
                                <div style={styles.convRow}><span>Total Value:</span> <span>{inrValue.toLocaleString()} {localCurrency}</span></div>
                                <div style={{...styles.convRow, color: theme.brand, marginTop: '8px', fontSize: '14px'}}><span>Payable:</span> <strong>{assetValue.toFixed(4)} {paymentMethod || 'Asset'}</strong></div>
                            </div>

                            <div style={{...styles.walletCopy, backgroundColor: theme.card, border: `1px dashed ${theme.border}`}}>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <div style={{fontSize: '9px', textTransform: 'uppercase', opacity: 0.5, marginBottom: '4px'}}>Address</div>
                                    <code style={{ fontSize: '11px', color: theme.text, wordBreak: 'break-all' }}>{activeAddress}</code>
                                </div>
                                <div onClick={() => handleCopy(activeAddress)} style={{...styles.copyActionIcon, background: theme.brand + '20', color: theme.brand}}>
                                    <FiCopy />
                                </div>
                            </div>

                            <button disabled={isBelowMinimum || !paymentMethod} style={{...styles.payBtn, backgroundColor: theme.brand, opacity: (isBelowMinimum || !paymentMethod) ? 0.5 : 1}} onClick={() => setScreen('submit')}>I HAVE TRANSFERRED</button>
                        </>
                    )}

                    {screen === 'apps' && (
                        <div style={{textAlign: 'left'}}>
                            {financeData.filter(i => i.type === 'thirdParty' || i.thirdParty).map((item, idx) => {
                                const details = item.thirdParty || item;
                                return (
                                    <div key={item._id || idx} style={{...styles.detailCard, backgroundColor: theme.card, borderColor: theme.border}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                            <div>
                                                <div style={{color: theme.brand, fontWeight: '900', fontSize: '15px'}}>{details.appName || "App"}</div>
                                                <div style={{fontSize: '14px', fontWeight: '700', margin: '4px 0'}}>{details.details || details.upiId}</div>
                                                <div style={{fontSize: '11px', color: theme.subText}}>Name: {details.receiverName}</div>
                                            </div>
                                            <button onClick={() => handleCopy(details.details || details.upiId)} style={{...styles.copyRoundBtn, background: theme.brand + '20', color: theme.brand}}>
                                                <FiCopy />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <button style={{...styles.payBtn, backgroundColor: theme.brand, marginTop: '10px'}} onClick={() => setScreen('submit')}>PROCEED TO VERIFY</button>
                        </div>
                    )}

                    {screen === 'bank' && (
                        <div style={{textAlign: 'left'}}>
                            {financeData.filter(i => i.type === 'bank' || i.bank).map((item, idx) => {
                                const bank = item.bank || item;
                                return (
                                    <div key={item._id || idx} style={{...styles.detailCard, backgroundColor: theme.card, borderColor: theme.border}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                                            <div style={{color: theme.accent, fontWeight: '900', fontSize: '16px'}}>{bank.bankName}</div>
                                            <button 
                                                onClick={() => handleCopy(`${bank.accountNumber} ${bank.ifsc}`)} 
                                                style={{...styles.miniBtn, background: theme.accent + '15', borderColor: theme.accent, color: theme.accent}}
                                            >
                                                <FiCopy /> COPY
                                            </button>
                                        </div>
                                        <div style={styles.bankList}>
                                            <div style={styles.bankItem}><span>A/C</span> <strong>{bank.accountNumber}</strong></div>
                                            <div style={styles.bankItem}><span>IFSC</span> <strong>{bank.ifsc}</strong></div>
                                            <div style={styles.bankItem}><span>Name</span> <strong>{bank.receiverName}</strong></div>
                                        </div>
                                    </div>
                                );
                            })}
                            <button style={{...styles.payBtn, backgroundColor: theme.brand, marginTop: '10px'}} onClick={() => setScreen('submit')}>I HAVE COMPLETED BANKING</button>
                        </div>
                    )}

                    {screen === 'submit' && (
                        <div style={{padding: '10px 0', textAlign: 'center'}}>
                            <div style={{width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px'}}>
                                <ShieldIcon color="#10b981" />
                            </div>
                            <h4 style={{color: theme.text, marginBottom: '8px'}}>Confirm Deposit</h4>
                            <p style={{fontSize: '12px', color: theme.subText, marginBottom: '20px'}}>Enter your Reference ID or Transaction Hash for verification.</p>
                            <input style={{...styles.inputFull, backgroundColor: theme.card, color: theme.text, borderColor: theme.brand}} placeholder="Paste Reference ID Here" value={txid} onChange={(e) => setTxid(e.target.value)} />
                            <button disabled={isSubmitting} style={{...styles.payBtn, backgroundColor: theme.brand, marginTop: '15px', boxShadow: `0 8px 25px ${theme.brand}40`}} onClick={handleVerifyClick}>
                                {isSubmitting ? "AUDITING..." : "SUBMIT FOR REVIEW"}
                            </button>
                        </div>
                    )}

                    {screen === 'verifying' && (
                        <div style={styles.verifyingContainer}>
                            <div style={{...styles.loader, borderTopColor: theme.brand}}></div>
                            <h3 style={{color: theme.text, margin: '20px 0 10px'}}>Audit Pending</h3>
                            <p style={{fontSize: '13px', color: theme.subText, lineHeight: '1.6'}}>Your request is in the queue. Funds reflect after 5-15 mins of network confirmation.</p>
                            <button style={{...styles.payBtn, backgroundColor: theme.card, border: `1px solid ${theme.border}`, marginTop: '25px'}} onClick={onClose}>RETURN TO PORTAL</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(10px)' },
    modalCard: { width: '92%', maxWidth: '400px', borderRadius: '32px', overflow: 'hidden', boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.6)', position: 'relative' },
    toast: { position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)', padding: '8px 16px', borderRadius: '12px', color: '#fff', fontSize: '10px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 100, backdropFilter: 'blur(10px)', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
    headerAction: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px' },
    headerTitle: { fontWeight: '900', fontSize: '15px', letterSpacing: '1.5px', opacity: 0.7 },
    iconBtn: { background: 'none', border: 'none', fontSize: '10px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' },
    body: { padding: '24px' },
    selectionTitle: { fontSize: '22px', fontWeight: '900', marginBottom: '10px', textAlign: 'center', letterSpacing: '-0.5px' },
    methodBtn: { width: '100%', padding: '8px 12px', borderRadius: '22px', border: '1px solid', fontSize: '14px', display: 'flex', alignItems: 'center', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', gap: '10px' },
    iconCircle: { width: '48px', height: '48px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    row: { display: 'flex', gap: '10px', marginBottom: '12px' },
    input: { flex: 1, padding: '14px', borderRadius: '16px', border: '1px solid', fontWeight: '700', outline: 'none' },
    inputFull: { width: '100%', padding: '16px', borderRadius: '16px', border: '1px solid', marginBottom: '8px', boxSizing: 'border-box', fontWeight: '800', textAlign: 'center', fontSize: '18px', outline: 'none' },
    conversionBox: { padding: '16px', borderRadius: '20px', textAlign: 'left', marginBottom: '15px', border: '1px solid' },
    convRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600' },
    walletCopy: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', borderRadius: '18px', marginBottom: '20px' },
    copyActionIcon: { padding: '10px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    detailCard: { padding: '18px', borderRadius: '22px', marginBottom: '12px', border: '1px solid' },
    bankList: { marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
    bankItem: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '4px' },
    copyRoundBtn: { border: 'none', width: '38px', height: '38px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    payBtn: { width: '100%', padding: '18px', color: '#fff', border: 'none', borderRadius: '20px', fontWeight: '900', cursor: 'pointer', letterSpacing: '0.5px', transition: 'all 0.2s' },
    miniBtn: { border: '1px solid', padding: '6px 12px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' },
    loader: { width: '45px', height: '45px', border: '4px solid rgba(255,255,255,0.05)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' },
    verifyingContainer: { padding: '20px 0', textAlign: 'center' }
};

export default PaymentModal;